import * as vp from 'vopay';
import {
  Box,
  Card,
  Title,
  Pagination,
  GetColorStatus,
  Icon,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { format, parseISO } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchScheduledTransactions from '@/hooks/useApi/useFetchScheduledTransactions';
import ScheduledTransactionsForm from './ScheduledTransactionsForm';
import styles from './ScheduledTransactions.module.scss';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { TableFiltersContainer, TransactionStatus, Table } from 'UI';

type QueryParams = {
  scheduleType: string;
  transactionType: string;
  frequency: string;
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
};

type ScheduledTransactionSearchParams = {
  scheduleType?: string;
  transactionType?: string;
  frequency?: string;
  status?: string;
  searchTerm?: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
};

type FC = FunctionComponent;

const ScheduledTransactionsContainer: FC = () => {
  const { response } = useClientData();
  const location = useLocation();
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.List' });

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const scheduleType = useMemo(() => queryParams.get('scheduleType') || '', [queryParams]);
  const transactionType = useMemo(() => queryParams.get('transactionType') || '', [queryParams]);
  const status = useMemo(() => queryParams.get('status') || '', [queryParams]);
  const frequency = useMemo(() => queryParams.get('frequency') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || '', [queryParams]);
  const timeDifference = moment().subtract(1, 'week').utc().format('YYYY-MM-DD');
  const currentDateTime = moment().utc().format('YYYY-MM-DD');
  const filtersApplied = useMemo(() => {
    return Boolean(
      transactionType ||
        status ||
        frequency ||
        searchTerm ||
        (startDate && startDate !== timeDifference) ||
        (endDate && endDate !== currentDateTime),
    );
  }, [transactionType, status, frequency, searchTerm, startDate, endDate, timeDifference, currentDateTime]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const [initialLoad, setInitialLoad] = useState(false);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<vp.ScheduledTransactionsList[]>([]);
  const [scheduledTransactionsResponse, , , fetchScheduledTransactions] = useFetchScheduledTransactions({});

  const alertDialog = useAlertDialog();

  // pagination
  const totalDataLength = useMemo(() => {
    return scheduledTransactionsResponse.PaginationMetaData?.TotalRecords || 0;
  }, [scheduledTransactionsResponse.ScheduledTransactions]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: ScheduledTransactionSearchParams = {}) => {
    const defaultValues = {
      StartDate: format(searchParams.timeRange?.[0] ?? parseISO(startDate || timeDifference), 'yyyy-MM-dd'),
      EndDate: format(searchParams.timeRange?.[1] ?? parseISO(endDate || currentDateTime), 'yyyy-MM-dd'),
      ...(searchParams.scheduleType === 'all' ? {} : { Frequency: searchParams.scheduleType }),
      ...(searchParams.transactionType === 'all' ? {} : { ScheduleTransactionType: searchParams.transactionType }),
      ...(searchParams.frequency === 'all' ? {} : { NameOfFrequency: searchParams.frequency }),
      ...(searchParams.status === 'all' ? {} : { ScheduledTransactionStatus: searchParams.status }),
      SearchText: searchParams.searchTerm ?? searchTerm,
      ScheduledTransactionID: '',
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchScheduledTransactions(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const scheduledTransactionRequest = Object.values(res.ScheduledTransactions || {});
      setFilteredList(scheduledTransactionRequest);
      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('SearchForm.defaultFetchingErrorMessage');
      alertDialog.error('Error', msg);
      console.error(err);
    }
  };

  useEffect(() => {
    setHasInvalidEndDate(startDate > endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams = {
      scheduleType,
      transactionType,
      searchTerm,
      frequency,
      startDate: format(parseISO(startDate || timeDifference), 'yyyy-MM-dd'),
      endDate: format(parseISO(endDate || currentDateTime), 'yyyy-MM-dd'),
      status,
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  useEffect(() => {
    const ScheduledTransactionsRequest = Object.values(
      scheduledTransactionsResponse.ScheduledTransactions || {},
    ).reverse();
    setFilteredList(ScheduledTransactionsRequest);
  }, [scheduledTransactionsResponse.ScheduledTransactions]);

  const tableProps: vp.TableProps<vp.ScheduledTransactionsList> = {
    id: 'sheduled-transactions-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'id',
        header: t('Table.id'),
        align: 'left',
        template: ({ data }) => {
          return (
            <Table.Link
              to={{
                pathname: `/scheduled-transactions/details/${data.ID}`,
                state: {
                  transactionId: data.ID,
                },
              }}
            >
              {data.ID}
            </Table.Link>
          );
        },
      },
      {
        id: 'name',
        header: t('Table.name'),
        align: 'left',
        template: ({ data }) =>
          data.ScheduledDetails.CompanyName ? (
            <>{data.ScheduledDetails.CompanyName}</>
          ) : (
            <>
              {data.ScheduledDetails.FirstName} {data.ScheduledDetails.LastName}
            </>
          ),
      },
      {
        id: 'scheduleType',
        header: t('Table.scheduleType'),
        align: 'left',
        template: ({ data }) =>
          data.Frequency ? t(`SearchForm.ScheduleType.Options.${data.Frequency}`, { defaultValue: '' }) : '',
      },

      {
        id: 'transactionType',
        header: t('Table.transactionType'),
        align: 'left',
        template: ({ data }) =>
          data.Type ? t(`SearchForm.TransactionType.Options.${data.Type}`, { defaultValue: '' }) : '',
      },

      {
        id: 'frequency',
        header: t('Table.frequency'),
        align: 'left',
        template: ({ data }) =>
          data.NameOfFrequency ? t(`SearchForm.Frequency.Options.${data.NameOfFrequency}`, { defaultValue: '' }) : '',
      },
      {
        id: 'amount',
        header: t('Table.amount'),
        align: 'left',
        template: ({ data }) =>
          (
            <>
              {response.Country === 'CA' ? 'CAD' : 'USD'} ${formattedCurrency(data.Amount)}
            </>
          ) || '',
      },
      { id: 'startDate', header: t('Table.startDate'), align: 'left', field: 'StartDate' },
      {
        id: 'ending',
        header: t('Table.ending'),
        align: 'left',
        template: ({ data }) =>
          data.EndDate !== ''
            ? data.EndDate
            : data.EndingAfterPayments !== ''
              ? t('Table.endingAfterPayments', { counts: data.EndingAfterPayments })
              : '',
      },
      {
        id: 'account',
        header: t('Table.account'),
        align: 'left',
        template: ({ data }) => data.ScheduledDetails.AccountNumber,
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status || '')}
              label={t(`SearchForm.Status.Options.${data.Status}`, { defaultValue: '' })}
            />
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.ScheduledTransactionsList } => ({
      id: String(item.ID || ''),
      data: item,
    })),
  };

  const EmptyOrNoResults = () => {
    const isEmpty = initialLoad && tableProps.rows?.length === 0;
    const isNoSearch = !initialLoad;

    const message = isNoSearch ? t('Table.Messages.useSearchTool') : t('Table.Messages.noRecordsToDisplay');

    return isEmpty || isNoSearch ? (
      <Box flx="col" className={styles.scheduledTableContainer} width="100%" gap="common">
        <Box flx="col" jc="center" gap="sm">
          {isNoSearch && (
            <Box ai="center" jc="center" height="auto">
              <Icon icon="search2" size="lg" />
            </Box>
          )}
          <Box ai="center" jc="center" pt="md" height="auto">
            {message}
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  return (
    <>
      <Box flx="row" jc="center">
        <Box flx="col" gapSize="36px" width="100%">
          <Box jc="sb" ai="center" width="100%">
            <Title title={t('title')} />
          </Box>
          <Card
            style={{
              padding: '16px',
            }}
          >
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <TableFiltersContainer.Trigger />
                <TableFiltersContainer.Content>
                  <ScheduledTransactionsForm
                    scheduleType={scheduleType}
                    frequency={frequency}
                    status={status}
                    searchTerm={searchTerm}
                    transactionType={transactionType}
                    hasInvalidEndDate={hasInvalidEndDate}
                    shouldShowEndDateError={shouldShowEndDateError}
                    setShouldShowEndDateError={setShouldShowEndDateError}
                    initialTimeRange={[
                      parseISO(startDate || timeDifference).getTime() as vp.Timestamp,
                      parseISO(endDate || currentDateTime).getTime() as vp.Timestamp,
                    ]}
                    search={(searchParams) => {
                      const offset = setPageIndex(0);
                      updateQueryParams({
                        scheduleType: searchParams.scheduleType || '',
                        transactionType: searchParams.transactionType || '',
                        frequency: searchParams.frequency || '',
                        searchTerm: searchParams.searchTerm || '',
                        startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                        endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                        status: searchParams.status || '',
                      });
                      search(offset, searchParams);
                    }}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>
            <Box>
              <Table {...tableProps} />
            </Box>
            <EmptyOrNoResults />
            <Box width="100%" jc="center" mt="lg" mb="sm">
              {pageCount > 1 && (
                <Pagination
                  pageCount={pageCount}
                  forcePageNumber={pageIndex}
                  handlePageClick={(e) => {
                    const offset = setPageIndex(e.selected);
                    const searchParams = {
                      scheduleType,
                      transactionType,
                      searchTerm,
                      frequency,
                      startDate: format(new Date(startDate || timeDifference), 'yyyy-MM-dd'),
                      endDate: format(new Date(endDate || currentDateTime), 'yyyy-MM-dd'),
                      status,
                    };
                    search(offset, searchParams);
                  }}
                />
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default ScheduledTransactionsContainer;
